/**
 * Single source of truth for the "app" dimension (CRM / HRMS / TrackDox).
 *
 * The platform is multi-product; every service module and billing plan belongs
 * to exactly one app. Centralising the labels, icons and colors here keeps the
 * app switcher, badges and per-app revenue cards visually consistent and stops
 * pages from re-deriving the product by string-matching names.
 *
 * Values mirror the backend `APP_CHOICES` on ServiceModule / BillingPlan
 * (identity-center/apps/billing): "crm", "hrms", "trackdox".
 */

/** Ordered list of real apps (excludes the "all" meta-scope). */
export const APP_CHOICES = [
  {
    value: 'crm',
    label: 'CRM',
    fullName: 'Leadspoint CRM',
    icon: 'solar:phone-calling-rounded-linear',
    // Tailwind utility fragments for a consistent badge look.
    badge: 'bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800',
    dot: 'bg-blue-500',
    bar: 'bg-blue-500',
    // Hex for charts / inline styles.
    hex: '#3B82F6',
  },
  {
    value: 'hrms',
    label: 'HRMS',
    fullName: 'DigiHRMS',
    icon: 'solar:users-group-two-rounded-linear',
    badge: 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800',
    dot: 'bg-green-500',
    bar: 'bg-green-500',
    hex: '#10B981',
  },
  {
    value: 'trackdox',
    label: 'TrackDox',
    fullName: 'TrackDox',
    icon: 'solar:documents-linear',
    badge: 'bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800',
    dot: 'bg-purple-500',
    bar: 'bg-purple-500',
    hex: '#8B5CF6',
  },
];

/** The "all" meta-scope used by the app switcher tabs. */
export const APP_ALL = { value: 'all', label: 'All', icon: 'solar:widget-5-linear' };

/** Tab list for the app switcher: All + each real app. */
export const APP_TABS = [APP_ALL, ...APP_CHOICES];

/** Quick lookup map keyed by app value. */
export const APP_MAP = APP_CHOICES.reduce((acc, app) => {
  acc[app.value] = app;
  return acc;
}, {});

/**
 * Resolve app metadata by value. Returns a safe fallback for unknown values so
 * the UI never crashes on legacy/missing data.
 * @param {string} value
 * @returns {object}
 */
export const getAppMeta = (value) => {
  if (!value) return UNKNOWN_APP;
  return APP_MAP[String(value).toLowerCase()] || UNKNOWN_APP;
};

export const UNKNOWN_APP = {
  value: 'unknown',
  label: '—',
  fullName: 'Unknown',
  icon: 'solar:widget-linear',
  badge: 'bg-gray-100 text-gray-500 border border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600',
  dot: 'bg-gray-400',
  bar: 'bg-gray-400',
  hex: '#9CA3AF',
};

/** Just the valid app values, e.g. for validation. */
export const APP_VALUES = APP_CHOICES.map((a) => a.value);
